import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ListChecks,
  ChevronRight,
  CircleDot,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { fetchApi } from "@/lib/api";
import {
  AgentTask,
  AgentTaskStatus,
  ListOpportunityTasksResponseSchema,
  AgentSdkThreadsResponseSchema,
} from "@forgepoint/shared";
import { useStandardAgentDisplayName } from "@/modules/standardAgent/useStandardAgentDisplayName";
import { cn } from "@/lib/utils";
import { getTaskSortPriority, isAgentOwned } from "./opportunityTaskUtils";
import { OpportunityTaskRow } from "./OpportunityTaskRow";

interface OpportunityTasksSectionProps {
  opportunityId: string;
  isEditing?: boolean;
  compact?: boolean;
  onThreadOpen?: (threadId: string) => void;
}

/**
 * Displays agent tasks scoped to an opportunity, grouped by status priority.
 * Human-owned and unassigned current work stays visible.
 * Agent work, upcoming tasks and history are collapsed by default.
 *
 * @param opportunityId - The ID of the opportunity to fetch tasks for
 * @param isEditing - Whether the opportunity is in edit mode. When true, the component is hidden.
 */
export const OpportunityTasksSection: React.FC<
  OpportunityTasksSectionProps
> = ({ opportunityId, isEditing = false, compact = false, onThreadOpen }) => {
  const navigate = useNavigate();
  const agentName = useStandardAgentDisplayName();
  const [tasks, setTasks] = useState<AgentTask[]>([]);
  const [inaccessibleThreadIds, setInaccessibleThreadIds] = useState<
    Set<string>
  >(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(true);
  const [showAgentTasks, setShowAgentTasks] = useState(false);
  const [showUpcoming, setShowUpcoming] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);

  useEffect(() => {
    let stale = false;

    const fetchThreadAccess = async () => {
      const response = await fetchApi("standard-agent/threads", "GET", {
        params: { opportunityId },
      });
      if (!stale && response.ok) {
        const parsed = AgentSdkThreadsResponseSchema.parse(response.data);
        setInaccessibleThreadIds(
          new Set(
            parsed.threads
              .filter((t) => t.accessible === false)
              .map((t) => t.id),
          ),
        );
      }
    };

    fetchThreadAccess();

    return () => {
      stale = true;
    };
  }, [opportunityId]);

  useEffect(() => {
    let stale = false;

    setShowAgentTasks(false);
    setShowUpcoming(false);
    setShowCompleted(false);
    setShowCancelled(false);

    const fetchTasks = async () => {
      setIsLoading(true);
      try {
        const response = await fetchApi(
          `opportunities/${opportunityId}/tasks`,
          "GET",
        );
        if (!stale && response.ok) {
          const parsed = ListOpportunityTasksResponseSchema.parse(
            response.data,
          );
          setTasks(parsed.tasks);
        }
      } finally {
        if (!stale) {
          setIsLoading(false);
        }
      }
    };

    fetchTasks();

    return () => {
      stale = true;
    };
  }, [opportunityId]);

  if (isEditing) {
    return null;
  }

  /**
   * Opens a standard agent thread from the opportunity page.
   * @param threadId - The ID of the thread to navigate to
   */
  const handleThreadClick = (threadId: string) => {
    if (onThreadOpen) {
      onThreadOpen(threadId);
      return;
    }
    navigate(`/standard-agent?threadId=${threadId}`);
  };

  const sortedTasks = [...tasks].sort(
    (a, b) =>
      getTaskSortPriority(a) - getTaskSortPriority(b) ||
      b.updatedAt.getTime() - a.updatedAt.getTime(),
  );

  const activeTasks = sortedTasks.filter(
    (t) => t.status === AgentTaskStatus.IN_PROGRESS,
  );
  const currentTasks = activeTasks.filter((task) => !isAgentOwned(task));
  const agentTasks = activeTasks.filter(isAgentOwned);
  const upcomingTasks = sortedTasks.filter(
    (t) => t.status === AgentTaskStatus.PENDING,
  );
  const completedTasks = sortedTasks.filter(
    (t) => t.status === AgentTaskStatus.COMPLETED,
  );
  const cancelledTasks = sortedTasks.filter(
    (t) => t.status === AgentTaskStatus.CANCELLED,
  );

  return (
    <div className={compact ? "" : "border-t pt-6"}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div
          className={cn(
            "flex items-center justify-between",
            compact ? "mb-3" : "mb-4",
          )}
        >
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronRight
                className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform duration-200",
                  isOpen && "rotate-90",
                )}
              />
              <h3
                className={cn("font-semibold", compact ? "text-sm" : "text-lg")}
              >
                Tasks
              </h3>
              {!isLoading && tasks.length > 0 && (
                <Badge variant="secondary" className="text-xs tabular-nums">
                  {tasks.length}
                </Badge>
              )}
            </button>
          </CollapsibleTrigger>
        </div>

        <CollapsibleContent>
          {isLoading ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-2/3" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                  <Skeleton className="h-8 w-8 rounded-md ml-2 shrink-0" />
                </div>
              ))}
            </div>
          ) : tasks.length === 0 ? (
            <div
              className={cn(
                "flex flex-col items-center justify-center px-4 border rounded-lg text-center",
                compact ? "py-4" : "py-8",
              )}
            >
              <ListChecks className="h-7 w-7 text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground">
                No tasks awaiting human action
              </p>
              <p className="text-xs text-muted-foreground/60 mt-0.5">
                Agent tasks will appear here
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border divide-y">
              {currentTasks.length === 0 && (
                <p className="px-3 py-3 text-sm text-muted-foreground">
                  No tasks awaiting human action
                </p>
              )}
              {currentTasks.map((task) => (
                <OpportunityTaskRow
                  key={task.id}
                  task={task}
                  agentName={agentName}
                  onThreadClick={handleThreadClick}
                  threadAccessible={
                    !task.threadId || !inaccessibleThreadIds.has(task.threadId)
                  }
                />
              ))}
              {agentTasks.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowAgentTasks((prev) => !prev)}
                    aria-expanded={showAgentTasks}
                    className="flex items-center gap-1.5 w-full py-2.5 px-3 text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                  >
                    <ChevronRight
                      className={cn(
                        "h-3 w-3 transition-transform duration-200",
                        showAgentTasks && "rotate-90",
                      )}
                    />
                    <CircleDot className="h-3 w-3 text-muted-foreground" />
                    <span>
                      {showAgentTasks ? "Hide" : "Show"} {agentTasks.length}{" "}
                      task{agentTasks.length !== 1 && "s"} with {agentName}
                    </span>
                  </button>
                  {showAgentTasks && (
                    <div className="divide-y border-t">
                      {agentTasks.map((task) => (
                        <OpportunityTaskRow
                          key={task.id}
                          task={task}
                          agentName={agentName}
                          onThreadClick={handleThreadClick}
                          threadAccessible={
                            !task.threadId ||
                            !inaccessibleThreadIds.has(task.threadId)
                          }
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
              {upcomingTasks.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowUpcoming((prev) => !prev)}
                    aria-expanded={showUpcoming}
                    className="flex items-center gap-1.5 w-full py-2.5 px-3 text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                  >
                    <ChevronRight
                      className={cn(
                        "h-3 w-3 transition-transform duration-200",
                        showUpcoming && "rotate-90",
                      )}
                    />
                    <CircleDot className="h-3 w-3 text-muted-foreground" />
                    <span>
                      {showUpcoming ? "Hide" : "Show"} {upcomingTasks.length}{" "}
                      upcoming task{upcomingTasks.length !== 1 && "s"}
                    </span>
                  </button>
                  {showUpcoming && (
                    <div className="divide-y border-t">
                      {upcomingTasks.map((task) => (
                        <OpportunityTaskRow
                          key={task.id}
                          task={task}
                          agentName={agentName}
                          onThreadClick={handleThreadClick}
                          threadAccessible={
                            !task.threadId ||
                            !inaccessibleThreadIds.has(task.threadId)
                          }
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
              {completedTasks.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowCompleted((prev) => !prev)}
                    aria-expanded={showCompleted}
                    className="flex items-center gap-1.5 w-full py-2.5 px-3 text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                  >
                    <ChevronRight
                      className={cn(
                        "h-3 w-3 transition-transform duration-200",
                        showCompleted && "rotate-90",
                      )}
                    />
                    <CheckCircle2 className="h-3 w-3 text-muted-foreground" />
                    <span>
                      {showCompleted ? "Hide" : "Show"} {completedTasks.length}{" "}
                      completed
                    </span>
                  </button>
                  {showCompleted && (
                    <div className="divide-y border-t">
                      {completedTasks.map((task) => (
                        <OpportunityTaskRow
                          key={task.id}
                          task={task}
                          agentName={agentName}
                          onThreadClick={handleThreadClick}
                          threadAccessible={
                            !task.threadId ||
                            !inaccessibleThreadIds.has(task.threadId)
                          }
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
              {cancelledTasks.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowCancelled((prev) => !prev)}
                    aria-expanded={showCancelled}
                    className="flex items-center gap-1.5 w-full py-2.5 px-3 text-xs text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                  >
                    <ChevronRight
                      className={cn(
                        "h-3 w-3 transition-transform duration-200",
                        showCancelled && "rotate-90",
                      )}
                    />
                    <XCircle className="h-3 w-3 text-muted-foreground" />
                    <span>
                      {showCancelled ? "Hide" : "Show"} {cancelledTasks.length}{" "}
                      cancelled
                    </span>
                  </button>
                  {showCancelled && (
                    <div className="divide-y border-t">
                      {cancelledTasks.map((task) => (
                        <OpportunityTaskRow
                          key={task.id}
                          task={task}
                          agentName={agentName}
                          onThreadClick={handleThreadClick}
                          threadAccessible={
                            !task.threadId ||
                            !inaccessibleThreadIds.has(task.threadId)
                          }
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
